// Resolve a service base URL from the environment.
//
// A missing var used to interpolate the literal string "undefined" into
// the path, which axios then resolved against `config.api.baseURL` — so
// the request quietly went to the wrong host. `envBaseUrl` keeps the URL
// absolute and warns once per var, so a missing value shows up in the
// console instead of turning into a mystery 404. The fallback is the
// app's generic API host, the only host we can assume is configured.
const warnedBaseUrls = new Set()

function envBaseUrl(name, value, fallback = import.meta.env.VITE_API_BASE_URL) {
  const clean = (v) => String(v ?? '').replace(/\/+$/, '')
  if (value) return clean(value)
  if (!warnedBaseUrls.has(name)) {
    warnedBaseUrls.add(name)
    console.warn(
      `[api] ${name} is not set — falling back to ${fallback}. ` +
        'Copy .env.example to .env and fill in the missing value.'
    )
  }
  return clean(fallback)
}

const CUSTOMER_URL = envBaseUrl(
  'VITE_APP_CUSTOMER_API_URL',
  import.meta.env.VITE_APP_CUSTOMER_API_URL
)
const GLOBAL_URL = envBaseUrl('VITE_APP_GLOBAL_API_URL', import.meta.env.VITE_APP_GLOBAL_API_URL)
const GUARDIAN_URL = envBaseUrl(
  'VITE_APP_GUARDIAN_API_URL',
  import.meta.env.VITE_APP_GUARDIAN_API_URL
)
const AUTH_URL = envBaseUrl(
  'VITE_APP_AUTHENTICATION_API_URL',
  import.meta.env.VITE_APP_AUTHENTICATION_API_URL
)
const MOVE_PORTAL_URL = import.meta.env.VITE_APP_MOVE_PORTAL_URL
// Worker backend (`mdg-guardian-worker`) hosts the Pusher private-channel
// auth endpoint (`POST /api/pusher/user`). Falls back to the guardian URL
// when the env var is missing so dev can point everything at the same
// host.
const WORKER_URL = envBaseUrl(
  'VITE_APP_WORKER_API_URL',
  import.meta.env.VITE_APP_WORKER_API_URL,
  GUARDIAN_URL
)
// Admin / help service — kept separate from VITE_API_BASE_URL so the help
// backend (e.g. `http://127.0.0.1:9009/api`) can sit on its own port.
// Hosts the Help catalog (`/help/modules`), the What's New feed
// (`/whats-new/public`) and the support-ticket endpoint.
const ADMIN_URL = envBaseUrl('VITE_APP_SUPER_ADMIN_URL', import.meta.env.VITE_APP_SUPER_ADMIN_URL)

const APIs = {
  // Auth
  AUTH: Object.freeze({
    login: `${AUTH_URL}/login/global`,
    me: `${CUSTOMER_URL}/guardian-users/me`,
    // Pusher user-auth endpoint — pusher-js POSTs here whenever it needs to
    // authenticate a private user channel. The request includes the
    // `Authorization` + `branch-id` headers configured in
    // `src/utils/pusher/index.js`. Now served by the worker service
    // (`mdg-guardian-worker`) — see `VITE_APP_WORKER_API_URL`.
    verifyPusher: `${WORKER_URL}/pusher/user`,
  }),

  // PRODUCTS
  PRODUCTS: {
    LIST: AUTH_URL + '/platforms',
  },

  // Users (customer-base URL, e.g. forgot-password flow)
  USERS: Object.freeze({
    forgotPasswordInitialize: `${CUSTOMER_URL}/users/forgotpassword-initialize`,
    forgotPasswordConfirmation: `${CUSTOMER_URL}/users/forgotpassword-confirmation`,
    changePassword: `${CUSTOMER_URL}/users/change-password`,
    resetPassword: `${CUSTOMER_URL}/guardian-users/reset-password`,
  }),

  // Role Permission Module
  ROLE_PERMISSION: Object.freeze({
    getList: CUSTOMER_URL + '/guardian-roles',
    createRole: CUSTOMER_URL + '/guardian-roles',
    getRoleById: CUSTOMER_URL + '/guardian-roles',
    updateRole: CUSTOMER_URL + '/guardian-roles',
    deleteRole: CUSTOMER_URL + '/guardian-roles/',
  }),

  // Guardian Modules (permissions tree)
  GUARDIAN_MODULES: Object.freeze({
    list: GLOBAL_URL + '/guardian-modules',
  }),

  // Guardian Users
  GUARDIAN_USERS: Object.freeze({
    list: CUSTOMER_URL + '/guardian-users',
    create: CUSTOMER_URL + '/guardian-users',
    export: CUSTOMER_URL + '/guardian-users/export',
    byUuid: (uuid) => `${CUSTOMER_URL}/guardian-users/${uuid}`,
    update: (uuid) => `${CUSTOMER_URL}/guardian-users/${uuid}`,
    delete: (uuid) => `${CUSTOMER_URL}/guardian-users/${uuid}`,
    profile: CUSTOMER_URL + '/guardian-users/profile',
    // Branch-scoped permission payload for the logged-in user.
    // GET /guardian-users/permission — requires the `branch-id` header;
    // the response body carries `{ type, permissions: [...] }` under `data`.
    permission: CUSTOMER_URL + '/guardian-users/permission',
  }),

  // Facilities
  FACILITIES: Object.freeze({
    list: GUARDIAN_URL + '/facilities',
    create: GUARDIAN_URL + '/facilities',
    update: (uuid) => `${GUARDIAN_URL}/facilities/${uuid}`,
    byUuid: (uuid) => `${GUARDIAN_URL}/facilities/${uuid}`,
    // PATCH /facilities/{uuid}/name — quick rename of the facility record
    // (no payload required except the new name).
    updateName: (uuid) => `${GUARDIAN_URL}/facilities/${uuid}/name`,
    // PATCH /facilities/hierarchy/{uuid}/name — rename a single sub-layer
    // or cold-room node within a facility's hierarchy (NOT the facility
    // root itself, which uses updateName above).
    hierarchyNodeRename: (uuid) => `${GUARDIAN_URL}/facilities/hierarchy/${uuid}/name`,
    // DELETE /facilities/hierarchy/{uuid} — deletes a single sub-layer
    // or cold-room node. The server cascades to descendants, so the
    // client only needs to invalidate the facility query after a 2xx.
    hierarchyNodeDelete: (uuid) => `${GUARDIAN_URL}/facilities/hierarchy/${uuid}`,
    // POST /facilities/with-hierarchy — creates-or-replaces the nested
    // facility tree (sub-layers + cold rooms) attached to a facility.
    withHierarchy: GUARDIAN_URL + '/facilities/with-hierarchy',
    // PUT /facilities/{uuid}/with-hierarchy — replaces the full
    // hierarchy for an existing facility. Used by the details-page
    // add-child modal after the user confirms. The uuid is required
    // in the path (the facility root itself already exists from step
    // 1 of the create flow).
    withHierarchyPut: (uuid) => `${GUARDIAN_URL}/facilities/${uuid}/with-hierarchy`,
    // GET /facilities/export?columns=…&orderBy=…&order=… — streams a
    // CSV of the current facility list. Shares the same filter surface
    // as `list` (per swagger) and ignores `page`/`limit` so the file
    // contains every matching row. `branch-id`, `platform`, and
    // `Authorization` are injected by the apiClient interceptor.
    export: GUARDIAN_URL + '/facilities/export',
    // GET /facilities/counts — aggregate counts powering the
    // Facilities-page filter chips (All / Alarm / Normal). Response
    // shape: `{ all, normal, alarm }`. Implicitly scoped to the active
    // branch via the `branch-id` header injected by the apiClient
    // interceptor — no caller-side filter params needed.
    counts: GUARDIAN_URL + '/facilities/counts',
    // Facility map — uploaded floor-plan image OR a drawn blueprint
    // + cold-room pins. The discriminator (`facilityMapType`,
    // `'UPLOADED' | 'DRAWN' | null`) is stored server-side and flips
    // implicitly on the first POST.
    //
    // GET  /facilities/{uuid}/map — read the current map state.
    //   data: { facility, facilityMapType, facilityMapPath (s3Key |
    //         null), facilityMapData (ShapeNode[] | null),
    //         coldRoomPins: [{ ...pin, coldRoomUuid }], downloadUrl
    //         (presigned S3 URL, UPLOADED mode only) }
    //
    // POST /facilities/{uuid}/map/upload — multipart upload of a
    //         single floor-plan image (image/*, ≤5MB). Forces the
    //         facility into the UPLOADED mode (clearing any drawn
    //         canvas) and returns
    //         { facilityMapPath, facilityMapType: 'UPLOADED',
    //           downloadUrl }.
    //
    // POST /facilities/{uuid}/map/draw — persist a freshly drawn
    //         blueprint + reconcile cold-room pins. Forces the
    //         facility into the DRAWN mode (clearing any uploaded
    //         image) and returns `{ facility, coldRoomPins }`.
    //
    // PUT  /facilities/{uuid}/map — partial update. Patches
    //         whichever of `facilityMapData` / `coldRoomPins` the
    //         caller supplied; omitted fields are left untouched.
    map: (uuid) => `${GUARDIAN_URL}/facilities/${uuid}/map`,
    uploadMap: (uuid) => `${GUARDIAN_URL}/facilities/${uuid}/map/upload`,
    drawMap: (uuid) => `${GUARDIAN_URL}/facilities/${uuid}/map/draw`,
    updateMap: (uuid) => `${GUARDIAN_URL}/facilities/${uuid}/map`,
    // Shareable Live Wall tokens — opaque, scoped, revocable.
    //
    // POST   /facilities/{uuid}/share-tokens
    //   body: { expiresIn?: '24h'|'7d'|'never', label?: string,
    //           coldRoomIds?: string[] }
    //   → { token, url, expiresAt }
    //
    // GET    /facilities/{uuid}/share-tokens
    //   → [{ token, url, label, expiresAt, createdAt, revokedAt,
    //        coldRoomIds }]
    //
    // PATCH  /facilities/share-tokens/{token}/revoke
    //   → { token, revokedAt }
    createShareToken: (uuid) => `${GUARDIAN_URL}/facilities/${uuid}/share-tokens`,
    listShareTokens: (uuid) => `${GUARDIAN_URL}/facilities/${uuid}/share-tokens`,
    revokeShareToken: (token) => `${GUARDIAN_URL}/facilities/share-tokens/${token}/revoke`,
  }),

  // Module Categories — file-management categories scoped per module.
  //
  // GET /api/module-category              – paginated list, scoped to
  //                                         the active branch via the
  //                                         `branch-id` header. Supports
  //                                         `search`, `moduleName`,
  //                                         `orderBy`, `order` (all
  //                                         optional).
  //                                         `moduleName=ALL_MODULES`
  //                                         disables the filter and
  //                                         returns rows from every
  //                                         module in the branch.
  // POST /api/module-category             – create a new category.
  //                                         Payload: `{ name,
  //                                         moduleName }`. `moduleName`
  //                                         is the DB ENUM key (e.g.
  //                                         `DASHBOARD`).
  // DELETE /api/module-category/{uuid}    – delete a category by uuid.
  //                                         `branch-id`, `platform`,
  //                                         and `Authorization` are
  //                                         injected by apiClient.
  MODULE_CATEGORY: Object.freeze({
    list: GUARDIAN_URL + '/module-category',
    create: GUARDIAN_URL + '/module-category',
    delete: (uuid) => `${GUARDIAN_URL}/module-category/${uuid}`,
  }),

  // Documents — upload + lifecycle for the document library.
  //
  // POST /api/documents                   – create a new document.
  //                                         Multipart FormData payload
  //                                         carrying the file plus the
  //                                         form fields (`name`,
  //                                         `moduleName`,
  //                                         `categoryUuid`,
  //                                         `entityUuid`, `expiry`,
  //                                         `tags`). Empty optional
  //                                         fields are dropped; tags
  //                                         are flattened as a
  //                                         comma-separated string. The
  //                                         `branch-id`, `platform`, and
  //                                         `Authorization` headers are
  //                                         injected by apiClient.
  //
  // GET  /api/documents                   – paged list with the full
  //                                         filter set (per swagger):
  //                                         `page`, `limit`, `search`,
  //                                         `modules` (CSV of ENUMs),
  //                                         `categoryUuid`, `validity`,
  //                                         `contentType`, `tag`,
  //                                         `linkedEntityUuid`,
  //                                         `version`,
  //                                         `uploadedBetween` (CSV
  //                                         from,to ISO), `sortBy`,
  //                                         `order`. Response shape:
  //                                         `{ success, message, data[],
  //                                         meta{ total, page, limit,
  //                                         totalPages } }`.
  DOCUMENTS: Object.freeze({
    list: GUARDIAN_URL + '/documents',
    create: GUARDIAN_URL + '/documents',
    updateVersion: (uuid) => `${GUARDIAN_URL}/documents/${uuid}`,
    details: (uuid) => `${GUARDIAN_URL}/documents/${uuid}/details`,
    export: GUARDIAN_URL + '/documents/export',
    counts: GUARDIAN_URL + '/documents/counts',
  }),

  // Reminders (facilities → reminders tab). The list endpoint is
  // paginated by the backend; the create endpoint takes the full
  // payload built in `AddReminderModal.handleSave`. The detail
  // endpoint powers the read-only reminder dialog opened from the
  // Reminders tab table. `cancel` is a PATCH action endpoint that
  // flips an ACTIVE reminder to CANCELLED; once cancelled the row
  // stays terminal (no reactivation path), so the UI hides the
  // button after the call lands.
  REMINDERS: Object.freeze({
    list: GUARDIAN_URL + '/reminders',
    create: GUARDIAN_URL + '/reminders',
    byUuid: (uuid) => `${GUARDIAN_URL}/reminders/${uuid}`,
    cancel: (uuid) => `${GUARDIAN_URL}/reminders/cancel/${uuid}`,
  }),

  // Dashboard summary endpoints backing the dashboard cards. These are
  // pre-aggregated reads over a window rather than raw row listings —
  // the Alarm Excursions chart asks for Warning / Critical counts per
  // cold room, and the Device Data card asks for one device's channel
  // statistics plus its latest reading.
  //
  // `states` is the one-shot snapshot behind the KPI row (active
  // devices / access points, warning / critical / unconfirmed alarms).
  // It is scoped by a `facilityUuid` query param rather than a path
  // segment, so it takes no argument here — the caller passes the param.
  //
  // `preferences` is the per-user, per-branch layout the Customize
  // Dashboard modal persists: which sections are on and which cold room
  // / device each configurable section points at. GET reads it, PUT
  // writes it. The payload is an opaque `preferenceJson` blob — the
  // endpoint stores and returns it verbatim.
  DASHBOARD: Object.freeze({
    alarmSummary: (facilityUuid) => `${GUARDIAN_URL}/dashboard/alarm/${facilityUuid}`,
    deviceSummary: (deviceUuid) => `${GUARDIAN_URL}/dashboard/device/${deviceUuid}`,
    coldRoomSummary: (coldRoomUuid) => `${GUARDIAN_URL}/dashboard/coldroom/${coldRoomUuid}`,
    states: () => `${GUARDIAN_URL}/dashboard/states`,
    preferences: () => `${GUARDIAN_URL}/dashboard/preferences`,
  }),

  // Manufacturers (devices/manufacturers)
  MANUFACTURERS: Object.freeze({
    list: GUARDIAN_URL + '/devices/manufacturers',
  }),

  // Sensors (devices/sensors)
  SENSORS: Object.freeze({
    list: GUARDIAN_URL + '/devices/sensors',
  }),

  // Report configurations (scheduled reports)
  REPORT_CONFIGURATIONS: Object.freeze({
    list: GUARDIAN_URL + '/report-configurations',
    create: GUARDIAN_URL + '/report-configurations',
    update: (uuid) => `${GUARDIAN_URL}/report-configurations/${uuid}`,
    byUuid: (uuid) => `${GUARDIAN_URL}/report-configurations/${uuid}`,
    remove: (uuid) => `${GUARDIAN_URL}/report-configurations/${uuid}`,
    // GET /report-configurations/export?columns=...&orderBy=...&order=...
    // Returns a `text/csv` blob the client downloads directly. Column
    // list repeats as `columns=col` so the backend header validator
    // accepts it as an array.
    export: `${GUARDIAN_URL}/report-configurations/export`,
  }),

  // Generated report log (one-off + scheduled outputs the user can
  // view / download / share).
  REPORTS: Object.freeze({
    list: GUARDIAN_URL + '/reports',
    byUuid: (uuid) => `${GUARDIAN_URL}/reports/${uuid}`,
    file: (fileId) => `${GUARDIAN_URL}/reports/files/${fileId}`,
    // GET /reports/download/{uuid} — returns a short-lived S3 presigned
    // URL the client navigates to (or anchors against) to fetch the
    // generated CSV/PDF file. Response shape:
    //   { success, message, data: { uuid, name, downloadUrl } }
    download: (uuid) => `${GUARDIAN_URL}/reports/download/${uuid}`,
    // GET /reports/statistics — KPI counts powering the page tiles.
    // Response shape:
    //   { success, message, data: { generated:{count,pdf,csv},
    //                               activeSchedules, processing, failed } }
    statistics: GUARDIAN_URL + '/reports/statistics',
    // GET /reports/export?columns=...&status=...&orderBy=...&order=...
    // Streams a `text/csv` blob the client downloads directly. Column
    // list repeats as `columns=col` so the backend header validator
    // accepts it as an array. `status` is forwarded as-is so the same
    // dropdown filter that narrows the table also narrows the export.
    export: `${GUARDIAN_URL}/reports/export`,
  }),

  // Asset Tags / V-Tags
  ASSET_TAGS: Object.freeze({
    list: GUARDIAN_URL + '/asset-tags',
    byUuid: (uuid) => `${GUARDIAN_URL}/asset-tags/${uuid}`,
    decouple: GUARDIAN_URL + '/asset-tags/decouple-device',
    couple: GUARDIAN_URL + '/asset-tags/couple-device',
    swapHistory: (uuid) => `${GUARDIAN_URL}/asset-tags/swap-history/${uuid}`,
    export: GUARDIAN_URL + '/asset-tags/export',
  }),

  // Alarm Profiles
  ALARM_PROFILES: Object.freeze({
    list: GUARDIAN_URL + '/alarm-profiles',
    create: GUARDIAN_URL + '/alarm-profiles',
    byUuid: (uuid) => `${GUARDIAN_URL}/alarm-profiles/${uuid}`,
    export: GUARDIAN_URL + '/alarm-profiles/export',
    // GET /alarm-profiles/timeline-history?deviceIds=48[,49,…] — every
    // alarm profile (template) ever linked to the given device(s), each
    // carrying its current `meta.maxValue` / `meta.minValue` and the
    // full `linkHistory` ladder. Powers the KPI chart's per-profile
    // dashed limit lines (the chart ignores the linkHistory today and
    // only paints one horizontal pair per profile; the timeline slice
    // is the next iteration).
    timelineHistory: GUARDIAN_URL + '/alarm-profiles/timeline-history',
  }),

  // Notifications — bell-dropdown inbox + per-row mark-as-read. Hosted
  // by the guardian backend (`mdg-guardian`) rather than the generic
  // `apiClient` base URL, matching every other guardian-scoped module.
  // The frontend's generic apiClient is configured against
  // `VITE_API_BASE_URL` which points at a separate service in dev;
  // notifications live with the rest of the alarm + facility endpoints
  // so the inbox hits the same backend as the page that surfaces the
  // event.
  NOTIFICATIONS: Object.freeze({
    list: GUARDIAN_URL + '/notifications',
    markRead: (id) => `${GUARDIAN_URL}/notifications/${id}/read`,
  }),

  // Alarm Profile Logs — paginated stream of triggered alarm events scoped
  // to a specific cold room. Powers the cold-room detail page's Alarm Processes
  // tab so the table reflects real backend events instead of the static
  // prototype seed.
  // GET /alarm-profile-log?page&limit&coldRoomId&orderBy&order
  // Response envelope: { success, message, data: AlarmProfileLog[],
  // meta: { total, page, limit, totalPages } }.
  // Each row carries `severity`, `status`, `currentValue` (sensorSlug +
  // sensorValue), `createdAt`, `resolvedAt`, `duration`, and the id/uuid
  // of the triggering device + alarm profile template.
  ALARM_PROFILE_LOGS: Object.freeze({
    list: GUARDIAN_URL + '/alarm-profile-log',
    timeline: GUARDIAN_URL + '/alarm-profile-log/timeline',
    // GET /alarm-profile-log/:uuid — full alarm-log DTO (status,
    // currentValue, alarmProfileDetails, source details,
    // alarmLogTimeline ladder, recipientsEmails).
    byUuid: (uuid) => `${GUARDIAN_URL}/alarm-profile-log/${uuid}`,
    // POST /alarm-profile-log/user-actions — append a user-action entry
    // (operator note / confirmation / corrective action / RCA / resumed)
    // to a single alarm log. Body: { alarmProfileLogId, status, note }.
    // The server persists the entry and returns the created record so
    // the client can refresh the timeline + DTO.
    userActions: GUARDIAN_URL + '/alarm-profile-log/user-actions',
    // GET /alarm-profile-log/export?columns=...&<filters>&orderBy&order
    // Streams a `text/csv` blob. The caller turns the response into a
    // Blob + download link. `columns` is sent as a repeatable query
    // param (`columns=foo&columns=bar`) so the backend header rule
    // accepts an array of column names.
    export: GUARDIAN_URL + '/alarm-profile-log/export',
    // PATCH /alarm-profile-log/:uuid/read — flip a single alarm log
    // row's isRead flag. Drives the inbox dropdown's per-row
    // "Mark as read" action; after the call lands, the row drops out
    // of the `readFilter=UNCONFIRMED` slice on the next refetch.
    markRead: (uuid) => `${GUARDIAN_URL}/alarm-profile-log/${uuid}/read`,
    // PATCH /alarm-profile-log/:uuid/archive — flip the row's
    // `isArchive` flag to true. Server returns the updated DTO; caller
    // invalidates the list cache so the row drops out of the Active
    // tab and shows up under Archive.
    archive: (uuid) => `${GUARDIAN_URL}/alarm-profile-log/${uuid}/archive`,
  }),

  // Devices
  DEVICES: Object.freeze({
    list: GUARDIAN_URL + '/devices',
    create: GUARDIAN_URL + '/devices',
    unattached: GUARDIAN_URL + '/devices/unattached',
    export: GUARDIAN_URL + '/devices/export',
    byUuid: (uuid) => `${GUARDIAN_URL}/devices/${uuid}`,
  }),

  // Access Points (paged list + sort + filter support)
  ACCESS_POINTS: Object.freeze({
    // GET /access-points?page&limit&search&status&manufacturerId&orderBy&order
    // Response shape: { success, message, data: [...], meta: { total, page, limit, totalPages } }.
    list: GUARDIAN_URL + '/access-points',
    // POST /access-points — register a new access point. JSON body with
    // { name, serialNumber, location, manufacturerId } (manufacturerId
    // matches the brand picked in the dropdown).
    create: GUARDIAN_URL + '/access-points',
    // GET /access-points/{uuid} — full access-point record used by the
    // row-level Edit action to prefill the registration modal.
    byUuid: (uuid) => `${GUARDIAN_URL}/access-points/${uuid}`,
    // PUT /access-points/{uuid} — partial access-point update with the
    // same JSON body as the create endpoint. Triggered from the row-level
    // Edit action; every field (name, serialNumber, location,
    // manufacturerId) is mutable.
    update: (uuid) => `${GUARDIAN_URL}/access-points/${uuid}`,
    // GET /access-points/export?columns=…&orderBy=…&order=…&search=… —
    // streams a CSV of the filtered access-point set. Pagination is
    // dropped server-side so the export contains every matching row.
    // Mirrors the devices export endpoint so the Sensors page can wire
    // both tabs through the same `Export CSV` action menu.
    export: GUARDIAN_URL + '/access-points/export',
  }),

  // External Modules
  EXTERNAL_MODULES: Object.freeze({
    list: GUARDIAN_URL + '/external-modules',
    create: GUARDIAN_URL + '/external-modules',
    // GET /external-modules/unattached — external modules not currently
    // coupled to any V-Tag. Mirrors the shape returned by
    // /devices/unattached so the couple/swap modal can swap the picker
    // depending on the V-Tag's `type` without each branch needing a
    // bespoke handling layer.
    unattached: GUARDIAN_URL + '/external-modules/unattached',
    // GET /external-modules/{uuid} — full external-module record used by
    // the Duplicate + Edit actions to prefill the registration modal.
    byUuid: (uuid) => `${GUARDIAN_URL}/external-modules/${uuid}`,
    // PUT /external-modules/{uuid} — partial external-module update via
    // multipart/form-data (same body shape as the create endpoint).
    // Triggered from the row-level Edit action; V-Tag coupling is owned
    // by the V-Tags tab, so the modal omits `autoCreateAssetTag` /
    // `assetTagName` from the PUT body in edit mode.
    update: (uuid) => `${GUARDIAN_URL}/external-modules/${uuid}`,
    // GET /external-modules/export?columns=…&orderBy=…&order=…&search=… —
    // streams a CSV of the filtered external-module set. Pagination is
    // dropped server-side so the export contains every matching row.
    // Mirrors the access-points export endpoint so the Sensors page can
    // wire both tabs through the same `Export CSV` action menu.
    export: GUARDIAN_URL + '/external-modules/export',
  }),

  // Input Modules
  INPUT_MODULES: Object.freeze({
    list: GUARDIAN_URL + '/input-modules',
    create: GUARDIAN_URL + '/input-modules',
    // GET /input-modules/input-types — paginated dropdown options for the
    // "Input type" picker (4–20 mA, 0–10 V, …). Returns
    // `{ id, uuid, name, slug }[]` so the modal can render labels while
    // persisting the slug as the form value.
    inputTypes: GUARDIAN_URL + '/input-modules/input-types',
    // GET /input-modules/unattached — input modules not currently coupled
    // to any V-Tag. Mirrors the shape returned by /devices/unattached so
    // the couple/swap modal's "New Device" picker can list input modules
    // when the V-Tag's `type` is INPUT_MODULE.
    unattached: GUARDIAN_URL + '/input-modules/unattached',
    // GET /input-modules/{uuid} — full input-module record used by the
    // Duplicate + Edit actions to prefill the registration modal.
    byUuid: (uuid) => `${GUARDIAN_URL}/input-modules/${uuid}`,
    // PUT /input-modules/{uuid} — partial input-module update via
    // multipart/form-data (same body shape as the create endpoint).
    // Triggered from the row-level Edit action; V-Tag coupling is owned
    // by the V-Tags tab, so the modal omits `autoCreateAssetTag` /
    // `assetTagName` from the PUT body in edit mode.
    update: (uuid) => `${GUARDIAN_URL}/input-modules/${uuid}`,
    // GET /input-modules/export?columns=…&orderBy=…&order=…&search=… —
    // streams a CSV of the filtered input-module set. Pagination is
    // dropped server-side so the export contains every matching row.
    // Mirrors the access-points / external-modules export endpoints so
    // the Sensors page can wire every tab through the same `Export CSV`
    // action menu.
    export: GUARDIAN_URL + '/input-modules/export',
  }),

  // Global Settings — user-scoped preferences (language, timezone, units,
  // notification channels, compliance flags). Auth + Platform + branch-id
  // headers are attached automatically by apiClient; the bearer token is
  // injected from the active session in the request interceptor.
  GLOBAL_SETTINGS: Object.freeze({
    get: GUARDIAN_URL + '/global-settings',
    update: GUARDIAN_URL + '/global-settings',
  }),

  // Activity log (paged list + filter support)
  ACTIVITY: Object.freeze({
    // GET /activity?page&limit&search&module&action&dateFrom&dateTo
    list: GUARDIAN_URL + '/activity',
    // GET /activity/export?page&limit&search&module&action&dateFrom&dateTo
    // Honours the same filter shape as `list` and returns a `text/csv`
    // blob. The caller turns the response into a Blob + download link.
    export: GUARDIAN_URL + '/activity/export',
  }),

  // Branches (paged list + filter support)
  BRANCHES: Object.freeze({
    list: CUSTOMER_URL + '/branches',
    detail: CUSTOMER_URL + '/branches',
    create: CUSTOMER_URL + '/branches',
    update: CUSTOMER_URL + '/branches',
    status: CUSTOMER_URL + '/branches',
    remove: CUSTOMER_URL + '/branches',
    export: `${CUSTOMER_URL}/branches/export`,
    count: `${CUSTOMER_URL}/branches/count`,
    byUuid: (uuid) => `${CUSTOMER_URL}/branches/${uuid}`,
  }),
  //"testing"

  // Cold Rooms — editor + detail
  COLD_ROOMS: Object.freeze({
    // GET /cold-rooms — paginated cold-room list. Powers filter
    // dropdowns on the alarms log view that need a `coldRoomId`
    // (numeric PK) backed by a human label.
    list: GUARDIAN_URL + '/cold-rooms',
    // GET /cold-rooms/{uuid} — single cold-room record (used by the
    // editor to hydrate the canvas with the saved sketch + name).
    byUuid: (uuid) => `${GUARDIAN_URL}/cold-rooms/${uuid}`,
    // PUT /cold-rooms/configure/:coldRoomUuid — Save & finish in the
    // editor. Body: { coldRoomName, elements, placedDevices[] }.
    configure: (uuid) => `${GUARDIAN_URL}/cold-rooms/configure/${uuid}`,
    // POST /cold-rooms/upload-image — multipart upload of a layout
    // image. The server pushes to S3 and returns `{ uuid, s3Key,
    // downloadUrl }`. The editor wraps `s3Key` as `s3://<s3Key>` for
    // `element.src` and bakes `downloadUrl` into `element.srcResolved`
    // for in-session rendering (the persisted `src` alone is NOT
    // loadable by Konva). Declared as a literal URL (not a function)
    // because the endpoint is module-scoped, not per-cold-room.
    uploadLayoutImage: GUARDIAN_URL + '/cold-rooms/upload-image',
    // GET /cold-rooms/devices/{uuid} — devices linked to a cold room.
    // Used by the KPI (Monitoring & Analytics) page's Devices dropdown;
    // the response carries the device list scoped to the picked cold
    // room. Same uuid convention as the rest of the cold-room API.
    devices: (uuid) => `${GUARDIAN_URL}/cold-rooms/devices/${uuid}`,
  }),

  // Device Datalogs — readings stream for the cold-room Data Log tab.
  // GET /device-datalogs?page&limit&orderBy=logDateTime&order=DESC
  // Response envelope: { success, message, data: DeviceDatalog[],
  // meta: { total, page, limit, totalPages } }.
  // Each `DeviceDatalog` carries `deviceId`, `deviceSerialNumber`,
  // `sensorSlug`, `label`, `sensorValue`, `logDateTime`, and a nested
  // `deviceDetails` object with the battery + signal snapshot.
  // `branch-id` + `platform` headers are injected by the apiClient
  // interceptor; callers only need to pass paging + sort args.
  //
  // GET /device-datalogs/export — CSV stream of the same filtered set;
  // ignores pagination and emits every column by default (or the
  // caller-supplied `columns` whitelist). Accepts the same
  // `deviceIds` / `sensorSlug` / `orderBy` / `order` query params.
  //
  // GET /device-datalogs/kpi/export?serialNumbers=A&serialNumbers=B
  //                                &sensorSlug=Temperature
  //                                &startDate=…&endDate=…
  // — KPI-page CSV export. Same shape as `/device-datalogs/filter`
  // but accepts `serialNumbers` as a repeatable query param so the
  // caller can dump every selected logger in one call. Streams a
  // `text/csv` blob; the client wraps it in a Blob + download link.
  DEVICE_DATALOGS: Object.freeze({
    list: GUARDIAN_URL + '/device-datalogs',
    export: GUARDIAN_URL + '/device-datalogs/export',
    filter: GUARDIAN_URL + '/device-datalogs/filter',
    kpiExport: GUARDIAN_URL + '/device-datalogs/kpi/export',
  }),

  // KPI / Monitoring & Analytics — multi-logger data analysis. Powers the
  // /kpi page (filter bar + chart + stats table). Page currently renders
  // from seed data; wire these endpoints when the backend lands.
  //
  // GET /kpi/series?facilityId&coldRoomId&loggerIds&from&to&parameter
  //   → time-series samples per logger for the chart.
  // GET /kpi/stats?facilityId&coldRoomId&loggerIds&from&to&parameter
  //   → per-logger Avg/Min/Max/MKT + alarm limits for the stats table.
  // GET /kpi/stats/export?<same filters>
  //   → CSV stream of the same stats rows.
  KPI: Object.freeze({
    series: GUARDIAN_URL + '/kpi/series',
    stats: GUARDIAN_URL + '/kpi/stats',
    export: GUARDIAN_URL + '/kpi/stats/export',
  }),
  // Public (auth-free) Live Wall endpoint. Reached by anonymous viewers
  // who opened a shareable link minted via /facilities/{uuid}/share-tokens.
  // The token itself is the credential — there is no bearer header on
  // these requests, and the public client (publicApiClient) skips the
  // standard Authorization + branch-id injection so a stale token in
  // localStorage can never poison the call.
  PUBLIC: Object.freeze({
    liveWall: (token) => `${GUARDIAN_URL}/public/live-wall/${token}`,
  }),

  // Maintenance Records — one row per completed maintenance visit
  // against a cold room. Powers the Maintenance tab's "Log maintenance
  // record" modal.
  //
  // POST /maintenance-records — multipart/form-data with the file
  // (optional) plus the JSON fields. The bare `body` fields mirror
  // `CreateMaintenanceRecordDto` on the API side; the nested reminder
  // block is flat-inlined (not nested) per the DTO convention. The
  // service uploads the file to S3 inline and stamps
  // `certificateFileId` on the new record.
  //
  // GET /maintenance-records — paginated list (paged + sortable via
  // the standard `page`/`limit`/`orderBy`/`order` query params).
  // GET /maintenance-records/:uuid — single record by uuid.
  // PUT /maintenance-records/:uuid — partial update.
  // DELETE /maintenance-records/:uuid — soft-delete a single record.
  // DELETE /maintenance-records — bulk delete by uuid list.
  //
  // `branch-id`, `platform`, and `Authorization` are injected by the
  // apiClient interceptor; callers don't need to set them.
  MAINTENANCE_RECORDS: Object.freeze({
    create: GUARDIAN_URL + '/maintenance-records',
    list: GUARDIAN_URL + '/maintenance-records',
    byUuid: (uuid) => `${GUARDIAN_URL}/maintenance-records/${uuid}`,
  }),

  // Maintenance Schedules — recurrence + reminder config attached to a
  // single cold room. Powers the Maintenance tab's "Edit schedule"
  // modal; one schedule per cold-room (the service upserts on the
  // unique index, so we always POST).
  //
  // POST /maintenance-schedules/cold-room/:coldRoomUuid — create-or-
  //   update the schedule for that cold-room. JSON body matching
  //   `CreateMaintenanceScheduleDto`: `interval`, `scope?`,
  //   `nextMaintenanceDate`, and the flat-inlined reminder block
  //   (`remindersEnabled`, `sendReminder`, `reminderRecipients[]`,
  //   `emailSubject?`, `emailMessage?`, `ifOverdue?`). The
  //   `coldRoomUuid` is read from the path — the DTO does NOT
  //   include it. Response envelope: `{ success, message, data }`.
  // GET  /maintenance-schedules/cold-room/:coldRoomUuid — fetch the
  //   schedule for a single cold-room (returns the persisted shape so
  //   the Edit modal can prefill on reopen).
  //
  // `branch-id`, `platform`, and `Authorization` are injected by the
  // apiClient interceptor; callers don't need to set them.
  MAINTENANCE_SCHEDULES: Object.freeze({
    forColdRoom: (uuid) => `${GUARDIAN_URL}/maintenance-schedules/cold-room/${uuid}`,
    byColdRoom: (uuid) => `${GUARDIAN_URL}/maintenance-schedules/cold-room/${uuid}`,
  }),
}

APIs.PORTALS = Object.freeze({
  move: MOVE_PORTAL_URL,
})

// Help — public read-only catalog of modules + per-module FAQ sections.
// Backed by the admin service (VITE_APP_SUPER_ADMIN_URL). The list endpoint
// takes a `language` query param; the detail endpoint returns the full
// tree (sections → questions) for a single module slug. The PDF export
// streams a generated `application/pdf` blob (no JSON wrapper) for a
// single module + language combination. The search endpoint runs the
// query against every published question and returns a flat hit list
// across modules.
APIs.HELP = Object.freeze({
  list: ADMIN_URL + '/help/modules',
  detail: (slug) => `${ADMIN_URL}/help/modules/${slug}`,
  pdf: (slug) => `${ADMIN_URL}/help/modules/${slug}/pdf`,
  search: ADMIN_URL + '/help/modules/search',
})

// What's New — public CMS feed (product updates, integrations,
// maintenance, events). Backed by the same admin service as the Help
// catalog above (VITE_APP_SUPER_ADMIN_URL). The /public endpoints are
// documented as CORS-open with `*` and accept an optional `language`
// query param. No auth headers are required server-side; we route
// through the shared `apiClient` for parity with the Help module
// (admin service tolerates the extra headers).
APIs.WHATS_NEW = Object.freeze({
  list: `${ADMIN_URL}/whats-new/public`,
  detail: (slug) => `${ADMIN_URL}/whats-new/public/${slug}`,
})

// Support tickets — created from the Help page "Email Support" modal and
// persisted by the support backend (VITE_APP_SUPPORT_API_URL). The server
// is intentionally a separate service from the customer / admin APIs so
// the support team can evolve their schema without touching the main
// front-end release cadence. `create` mirrors `CreateSupportTicketDto`:
//
//   {
//     companyName?: string  (optional, ≤255)
//     userName:    string   (required, ≤255)
//     email:       string   (required, valid email, ≤120)
//     phoneNumber?: string  (optional, ≤45)
//     description: string   (required, rich-text HTML)
//     status?:     'OPEN' | 'RESOLVED'  (defaults to OPEN server-side)
//   }
APIs.SUPPORT_TICKETS = Object.freeze({
  create: ADMIN_URL + '/support-tickets',
})

Object.freeze(APIs)
export { MOVE_PORTAL_URL, ADMIN_URL }
export default APIs
